import React from 'react';
import { Box, Card, CardContent, Container, Typography, IconButton, SxProps, Theme, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

interface AuthCardProps {
    children: React.ReactNode;
    title?: string;
    subtitle?: string;
    description?: string;
    showBackButton?: boolean;
    onBackClick?: () => void;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    sx?: SxProps<Theme>;
}

export const AuthCard: React.FC<AuthCardProps> = ({
    children,
    title,
    subtitle,
    description,
    showBackButton = false,
    onBackClick,
    maxWidth = 'sm',
    sx,
}) => {
    const { isDarkMode } = useAppTheme();
    const muiTheme = useTheme();

    return (
        <Container
            maxWidth={maxWidth}
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                py: { xs: 2, sm: 4 },
                px: { xs: 1, sm: 2 },
                background: muiTheme.palette.background.default,
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: {
                        xs: '100%',
                        sm: maxWidth === 'md' ? 600 : 400,
                        md: maxWidth === 'md' ? 600 : 400
                    },
                    mx: 'auto',
                    boxShadow: isDarkMode
                        ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                        : '0 8px 32px rgba(0, 0, 0, 0.15)', // Slightly stronger shadow for light mode
                    borderRadius: { xs: '12px', sm: '8px' },
                    overflow: 'hidden',
                    border: isDarkMode ? 'none' : '1px solid rgba(0, 0, 0, 0.12)', // Add border for light mode
                    backgroundColor: isDarkMode
                        ? muiTheme.palette.background.paper
                        : '#ffffff', // Pure white for light mode
                    minHeight: maxWidth === 'md' ? { xs: 'auto', sm: '520px', md: '570px' } : 'auto',
                    ...sx, // Apply custom sx styles
                }}
            >
                <CardContent sx={{
                    p: { xs: 3, sm: 4, md: 6 },
                    backgroundColor: isDarkMode
                        ? muiTheme.palette.background.paper
                        : '#ffffff', // Pure white for light mode
                    '&:last-child': {
                        paddingBottom: { xs: 4, sm: 5, md: 6 }
                    }
                }}>
                    {/* Header */}
                    {(showBackButton || title) && (
                        <Box mb="2rem">
                            {showBackButton && (
                                <Box display="flex" alignItems="center" mb={3}>
                                    <IconButton
                                        onClick={onBackClick}
                                        sx={{
                                            mr: 2,
                                            color: muiTheme.palette.text.secondary,
                                            backgroundColor: isDarkMode
                                                ? muiTheme.palette.background.paper
                                                : 'rgba(0, 0, 0, 0.04)', // Light gray for light mode
                                            '&:hover': {
                                                color: muiTheme.palette.text.primary,
                                                backgroundColor: isDarkMode
                                                    ? muiTheme.palette.background.paper
                                                    : 'rgba(0, 0, 0, 0.08)', // Darker gray for hover in light mode
                                            }
                                        }}
                                    >
                                        <ArrowBack />
                                    </IconButton>
                                    {title && (
                                        <Typography variant="h5" component="h1">
                                            {title}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {!showBackButton && title && (
                                <Box textAlign="center">
                                    {subtitle && (
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            gutterBottom
                                            sx={{
                                                fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                                                fontWeight: 700,
                                                color: muiTheme.palette.text.primary,
                                                mb: 0.5,
                                                letterSpacing: '-0.02em'
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="h5"
                                        component="h2"
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                            fontWeight: 600,
                                            color: muiTheme.palette.text.primary,
                                            mb: 0.5,
                                            letterSpacing: '-0.01em'
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    {description && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: muiTheme.palette.text.secondary,
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                fontWeight: 400,
                                                lineHeight: 1.5
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Content */}
                    {children}
                </CardContent>
            </Card>
        </Container>
    );
};
